import React from 'react';
import { AiInsights } from '../types/moodboard';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { X } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from 'recharts';
interface AiInsightsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insights: AiInsights;
  onHighlight?: (type: 'color' | 'tag', value: string | null) => void;
  activeHighlight?: {
    type: string;
    value: string;
  } | null;
}
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-black text-white px-3 py-2 text-[10px] font-heading tracking-widest uppercase" data-id="element-68">
        {payload[0].payload.label}
      </div>;
  }
  return null;
};
export function AiInsightsPanel({
  open,
  onOpenChange,
  insights,
  onHighlight,
  activeHighlight
}: AiInsightsPanelProps) {
  if (!open) return null;
  return <div className="fixed inset-0 z-50 bg-white flex flex-col lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)] lg:w-[400px] xl:w-[480px] lg:flex-shrink-0 lg:border-l lg:border-border lg:z-auto" data-id="element-69">
      <div className="px-6 sm:px-8 py-6 sm:py-8 border-b border-border flex items-center justify-between" data-id="element-70">
        <h2 className="font-heading font-light text-[14px] tracking-[0.3em] uppercase" data-id="element-71">
          INSIGHTS IA
        </h2>
        <button onClick={() => onOpenChange(false)} className="text-[#666] hover:text-black transition-colors" aria-label="Chiudi Insights" data-id="element-72">
          <X className="w-5 h-5" data-id="element-73" />
        </button>
      </div>

      <ScrollArea className="flex-1" data-id="element-74">
        <div className="px-6 sm:px-8 py-8 sm:py-10 space-y-12 sm:space-y-16" data-id="element-75">
          {/* Direction Summary - Strengthened */}
          <section className="space-y-5 border-l-2 border-black pl-5" data-id="element-76">
            <h4 className="font-heading font-bold text-[11px] tracking-[0.25em] uppercase text-black" data-id="element-77">
              VISIONE D'INSIEME
            </h4>
            <p className="text-[15px] font-sans leading-[1.7] text-[#1A1A1A]" data-id="element-78">
              {insights.summary}
            </p>
          </section>

          {/* Cohesion Score - Strengthened */}
          <section className="space-y-4" data-id="element-79">
            <div className="flex items-end justify-between mb-2" data-id="element-80">
              <h4 className="font-heading font-normal text-[10px] tracking-[0.25em] uppercase text-[#666]" data-id="element-81">
                COERENZA VISIVA
              </h4>
              <span className="font-heading font-bold text-[32px] leading-none tracking-tight" data-id="element-82">
                {insights.cohesionScore}%
              </span>
            </div>
            <div className="w-full h-[2px] bg-[#E5E5E5]" data-id="element-83">
              <div className="h-full bg-black transition-all duration-500" style={{
              width: `${insights.cohesionScore}%`
            }} data-id="element-84" />
            </div>
            <p className="text-[12px] text-[#666] font-sans" data-id="element-85">
              {insights.cohesionScore > 70 ? 'Gli elementi mostrano una forte coerenza visiva e tematica.' : 'Elementi eterogenei. Valuta di restringere il focus creativo.'}
            </p>
          </section>

          {/* Creative Direction Chart */}
          {insights.directionMap && insights.directionMap.length > 0 && <section className="space-y-6" data-id="element-86">
              <h4 className="font-heading font-normal text-[10px] tracking-[0.25em] uppercase text-[#666]" data-id="element-87">
                MAPPA DIREZIONALE
              </h4>
              <div className="h-[280px] w-full relative" data-id="element-88">
                {/* Axis Labels */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-4 text-[9px] font-heading tracking-[0.2em] text-[#999] uppercase" data-id="element-89">
                  Espressivo
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 -mb-4 text-[9px] font-heading tracking-[0.2em] text-[#999] uppercase" data-id="element-90">
                  Minimale
                </div>
                <div className="absolute top-1/2 left-0 -translate-y-1/2 -ml-2 -rotate-90 text-[9px] font-heading tracking-[0.2em] text-[#999] uppercase origin-left" data-id="element-91">
                  Tradizione
                </div>
                <div className="absolute top-1/2 right-0 -translate-y-1/2 -mr-2 rotate-90 text-[9px] font-heading tracking-[0.2em] text-[#999] uppercase origin-right" data-id="element-92">
                  Innovazione
                </div>

                <ResponsiveContainer width="100%" height="100%" data-id="element-93">
                  <ScatterChart margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
              }} data-id="element-94">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={true} horizontal={true} data-id="element-95" />
                    <XAxis type="number" dataKey="x" domain={[0, 100]} hide data-id="element-96" />
                    <YAxis type="number" dataKey="y" domain={[0, 100]} hide data-id="element-97" />
                    <Tooltip content={<CustomTooltip data-id="element-99" />} cursor={{
                  strokeDasharray: '3 3'
                }} data-id="element-98" />
                    <Scatter name="Direction" data={insights.directionMap} fill="#000" data-id="element-100">
                      {insights.directionMap.map((entry, index) => <Cell key={`cell-${index}`} fill="#000" data-id="element-101" />)}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </section>}

          <div className="border-t border-border" data-id="element-102" />

          {/* Themes with Counts */}
          <section className="space-y-4" data-id="element-103">
            <h4 className="font-heading font-normal text-[10px] tracking-[0.25em] uppercase text-[#666]" data-id="element-104">
              TEMI PRINCIPALI
            </h4>
            <div className="flex flex-col gap-3" data-id="element-105">
              {insights.themes.length > 0 ? insights.themes.map((theme, idx) => {
              const isActive = activeHighlight?.type === 'tag' && activeHighlight.value === theme;
              // Mock count for visual purposes, in reality would come from tagCounts
              const count = Math.max(1, 5 - idx);
              return <button key={idx} onClick={() => onHighlight?.('tag', theme)} className={`group flex items-center justify-between p-3 border transition-all ${isActive ? 'border-black bg-black text-white' : 'border-border bg-white hover:border-black'}`} data-id="element-106">
                      <span className="font-heading text-[11px] tracking-[0.15em] uppercase" data-id="element-107">
                        {theme}
                      </span>
                      <span className={`font-mono text-[10px] ${isActive ? 'text-white/70' : 'text-[#999] group-hover:text-black'}`} data-id="element-108">
                        {count}
                      </span>
                    </button>;
            }) : <p className="text-[12px] text-[#666] font-sans" data-id="element-109">
                  Servono più elementi.
                </p>}
            </div>
          </section>

          <div className="border-t border-border" data-id="element-110" />

          {/* Dominant Colors */}
          <section className="space-y-4" data-id="element-111">
            <h4 className="font-heading font-normal text-[10px] tracking-[0.25em] uppercase text-[#666]" data-id="element-112">
              PALETTE DOMINANTE
            </h4>
            {insights.dominantColors.length > 0 ? <div className="flex flex-wrap gap-4" data-id="element-113">
                {insights.dominantColors.map((color, idx) => {
              const isActive = activeHighlight?.type === 'color' && activeHighlight.value === color;
              return <button key={idx} className={`flex flex-col items-center gap-2 transition-opacity hover:opacity-80 ${isActive ? 'ring-2 ring-black ring-offset-2' : ''}`} onClick={() => onHighlight?.('color', color)} data-id="element-114">
                      <div className="w-12 h-12 border border-border" style={{
                  backgroundColor: color
                }} data-id="element-115" />
                      <span className="font-mono text-[9px] text-[#666] uppercase" data-id="element-116">
                        {color}
                      </span>
                    </button>;
            })}
              </div> : <p className="text-[12px] text-[#666] font-sans" data-id="element-117">
                Aggiungi una palette colori per iniziare.
              </p>}
          </section>

          <div className="border-t border-border" data-id="element-118" />

          {/* Materials */}
          <section className="space-y-4" data-id="element-119">
            <h4 className="font-heading font-normal text-[10px] tracking-[0.25em] uppercase text-[#666]" data-id="element-120">
              MATERIALI E TEXTURE
            </h4>
            <div className="flex flex-wrap gap-2" data-id="element-121">
              {insights.materials.length > 0 ? insights.materials.map((material, idx) => {
              const isActive = activeHighlight?.type === 'tag' && activeHighlight.value === material;
              return <button key={idx} onClick={() => onHighlight?.('tag', material)} className={`font-heading text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 transition-colors ${isActive ? 'bg-black text-white' : 'bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#E5E5E5]'}`} data-id="element-122">
                      {material}
                    </button>;
            }) : <p className="text-[12px] text-[#666] font-sans" data-id="element-123">
                  Nessun materiale individuato.
                </p>}
            </div>
          </section>

          <div className="border-t border-border" data-id="element-124" />

          {/* Keywords */}
          <section className="space-y-4" data-id="element-125">
            <h4 className="font-heading font-normal text-[10px] tracking-[0.25em] uppercase text-[#666]" data-id="element-126">
              KEYWORDS
            </h4>
            <div className="flex flex-wrap gap-x-4 gap-y-2" data-id="element-127">
              {insights.styleKeywords.length > 0 ? insights.styleKeywords.map((keyword, idx) => {
              const isActive = activeHighlight?.type === 'tag' && activeHighlight.value === keyword;
              return <button key={idx} onClick={() => onHighlight?.('tag', keyword)} className={`text-[12px] font-heading tracking-[0.1em] uppercase transition-colors ${isActive ? 'text-black font-bold' : 'text-[#666] hover:text-black'}`} data-id="element-128">
                      {keyword}
                    </button>;
            }) : <p className="text-[12px] font-heading tracking-[0.1em] uppercase text-[#666]" data-id="element-129">
                  Aggiungi più tag per estrarre le keywords.
                </p>}
            </div>
          </section>
        </div>
      </ScrollArea>
    </div>;
}